import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { ScrollView } from "tamagui";
import { z } from "zod";

import {
  Button,
  Footer,
  Header,
  Heading,
  Paragraph,
  RadioGroup,
  TextArea,
  YStack,
} from "@/components";

const CANCELLATION_REASONS = [
  { id: "no-video", label: "I do not want a video visit" },
  { id: "bad-location", label: "I'm not in a good location right now" },
  { id: "questions", label: "I have questions before I start" },
  { id: "other", label: "Other" },
] as const;

const CANCELLATION_REASON_OPTIONS = Object.fromEntries(
  CANCELLATION_REASONS.map((r) => [r.id, r.label]),
);

const feedbackSchema = z
  .object({
    selectedReason: z.string().min(1, "Please select a reason"),
    freeText: z.string().optional(),
  })
  .refine(
    (data) => data.selectedReason !== "other" || !!data.freeText?.trim(),
    { message: "Please tell us more", path: ["freeText"] },
  );

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface CuraiFeedbackProps {
  onClose: () => void;
  onReturnToVisit?: () => void;
}

const CuraiFeedback = ({ onClose, onReturnToVisit }: CuraiFeedbackProps) => {
  const { control, handleSubmit } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { selectedReason: "", freeText: "" },
  });

  const {
    field: selectedReasonField,
    fieldState: { error: selectedReasonError },
  } = useController({
    control,
    name: "selectedReason",
  });
  const {
    field: freeTextField,
    fieldState: { error: freeTextError },
  } = useController({
    control,
    name: "freeText",
  });

  const selectedReason = selectedReasonField.value;
  const freeText = freeTextField.value ?? "";

  const StickyFooter = (
    <Footer padding="$200" mode="dialog">
      <Button
        dataTrackingId="curai-feedback-submit-button"
        mode="primary"
        constrained
        onPress={handleSubmit(() => onClose())}
        extraTrackingContext={{
          reason: selectedReason,
          ...(selectedReason === "other" && { freeText }),
        }}
      >
        Submit and cancel visit
      </Button>

      <Button
        dataTrackingId="curai-feedback-return-link"
        mode="tertiary"
        onPress={onReturnToVisit}
      >
        Return to visit
      </Button>
    </Footer>
  );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flex: 1,
          gap: "$250",
          paddingHorizontal: "$200",
          paddingBottom: "$500",
        }}
      >
        <Header gap="$100">
          <Heading size="$lg">Why are you canceling?</Heading>
          <Paragraph size="$sm" color="$textSecondary">
            You will have 6 hours to change your mind before this visit expires.
          </Paragraph>
        </Header>

        <YStack gap="$150">
          <RadioGroup
            value={selectedReason ?? undefined}
            onValueChange={selectedReasonField.onChange}
            dataTrackingId="video-visit-cancellation-reason"
            options={CANCELLATION_REASON_OPTIONS}
            errorText={selectedReasonError?.message}
          >
            <RadioGroup.Props value={selectedReason ?? undefined}>
              {CANCELLATION_REASONS.map((reason) => (
                <RadioGroup.Item
                  key={reason.id}
                  id={reason.id}
                  value={reason.id}
                >
                  {reason.label}
                </RadioGroup.Item>
              ))}
            </RadioGroup.Props>
          </RadioGroup>

          {selectedReason === "other" && (
            <TextArea.Controlled
              placeholder="Please tell us more..."
              value={freeText}
              onChangeText={freeTextField.onChange}
              fontSize="$sm"
              numberOfLines={4}
              errorText={freeTextError?.message}
            />
          )}
        </YStack>
      </ScrollView>
      {StickyFooter}
    </>
  );
};

export default CuraiFeedback;
